import { useMemo, useState, useRef, useEffect } from "react";
import type { Table as ArrowTable } from "apache-arrow";
import { DatePivotParams } from "../../../entities/date_pivot_params";
import { PivotViewConfig } from "../../../entities/pivot_view_config";
import { useGetDatasetSchemaUpdatesSubscription } from "../../../services/perspective_api/__generated__/perspective_server-types";
import { usePerspective } from "../../../contexts/app/provider";
import { TransformedCustomViewControlsComponent } from "./component";
import { AuthenticationService } from "../../../services/perspective_api/authentication";

export { EditModeToggle } from "./edit_mode_toggle";

export const TransformedCustomViewControlsContainer = <
  T extends DatePivotParams = DatePivotParams,
>({
  controllerLoading,
  isChartView,
  params,
  setControllerLoading,
  setIsChartView,
  setParams,
  lockedSetId,
  comparedToSetId,
  viewControllerConfig,
  parsedData,
  sortConfig,
  setSortConfig,
  columnSchema,
  setColumnSchema,
  onBackendExport,
  inMemoryArrowTable,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  controllerLoading: boolean;
  isChartView: boolean;
  params: T;
  setControllerLoading: (loading: boolean) => void;
  setIsChartView: React.Dispatch<React.SetStateAction<boolean>>;
  setParams: (params: T) => void;
  lockedSetId: string | null;
  comparedToSetId: string | null;
  parsedData: any;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  setColumnSchema: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          dtype: string;
          min?: string;
          max?: string;
          unique_values?: string[];
          value_counts?: Record<string, number>;
        }
      >
    >
  >;
  onBackendExport?: (excludeDetailRows: boolean) => Promise<void>;
  inMemoryArrowTable?: ArrowTable | null;
}) => {
  const { perspectiveApiService } = usePerspective();
  const {
    datasetName,
    folder,
    dateHierarchyColumnFilter,
    excludedSegmentationsFilter,
    measureColumnFilter,
  } = viewControllerConfig;

  const subscriptionVariables = useMemo(
    () => ({
      datasetName,
      folder,
      lockedSetId,
      comparedToSetId,
      format: "parquet",
    }),
    [datasetName, folder, lockedSetId, comparedToSetId]
  );

  const [dateHierarchyColumns, setDateHierarchyColumns] = useState<any[]>([]);
  const [segmentColumns, setSegmentColumns] = useState<any[]>([]);
  const [measureColumns, setMeasureColumns] = useState<any[]>([]);
  const [filterColumns, setFilterColumns] = useState<any[]>([]);

  const [isSubscriptionSkipped, setIsSubscriptionSkipped] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Listen for token refresh events to clear subscription skip state
  useEffect(() => {
    const handleTokenRefresh = () => {
      console.info("Token refreshed, clearing subscription skip state");
      setIsSubscriptionSkipped(false);
    };

    AuthenticationService.setOnTokenRefresh(handleTokenRefresh);

    return () => {
      // Clean up the callback when component unmounts
      AuthenticationService.setOnTokenRefresh(() => {});
    };
  }, []);

  console.info(
    "[SchemaSubscription] Rendering with skip:", isSubscriptionSkipped,
    "variables:", subscriptionVariables
  );

  useGetDatasetSchemaUpdatesSubscription({
    client: perspectiveApiService.getClient(),
    variables: subscriptionVariables,
    onData: (data) => {
      const rawSchema = data.data?.data?.getDatasetSchemaUpdates?.schema;
      console.info(
        "[SchemaSubscription] onData fired",
        "rawSchema keys:", rawSchema ? Object.keys(rawSchema).length : "null/undefined",
        "subscriptionVariables:", subscriptionVariables
      );
      const columns: Record<
        string,
        { dtype: string; min?: string; max?: string; unique_values?: string[] }
      > = rawSchema ?? {};
      setControllerLoading(false);
      setDateHierarchyColumns(
        dateHierarchyColumnFilter
          ? (() => {
              const filteredColumns = Object.keys(columns).filter((column) =>
                Object.keys(dateHierarchyColumnFilter).includes(column)
              );
              return filteredColumns.length > 0
                ? filteredColumns.map((c) => ({
                    name: c,
                    caption: dateHierarchyColumnFilter[c] ?? c,
                    min: columns?.[c]?.min ?? "",
                    max: columns?.[c]?.max ?? "",
                  }))
                : Object.values(dateHierarchyColumnFilter).map((c) => ({
                    name: c,
                    caption: c,
                    min: "",
                    max: "",
                  }));
            })()
          : Object.keys(columns)
              .filter(
                (column) =>
                  columns[column].dtype ===
                  "Datetime(time_unit='us', time_zone='UTC')"
              )
              .map((c) => ({
                name: c,
                caption: c,
                min: columns?.[c]?.min ?? "",
                max: columns?.[c]?.max ?? "",
              }))
      );
      setSegmentColumns(
        Object.keys(columns)
          .filter(
            (column) =>
              columns[column].dtype === "String" &&
              !excludedSegmentationsFilter?.includes(column)
          )
          .map((c) => ({ name: c, caption: c }))
      );
      setMeasureColumns(
        measureColumnFilter
          ? typeof measureColumnFilter === "function"
            ? // If it's a function, call it with params and use the returned Record (or null)
              (() => {
                const filterResult = measureColumnFilter(params);
                return filterResult
                  ? Object.keys(columns)
                      .filter((column) =>
                        Object.keys(filterResult).includes(column)
                      )
                      .map((c) => ({ name: c, caption: filterResult[c] ?? c }))
                  : Object.keys(columns)
                      .filter(
                        (column) =>
                          columns[column].dtype === "Float64" ||
                          columns[column].dtype === "Int64"
                      )
                      .map((c) => ({ name: c, caption: c }));
              })()
            : // If it's a Record, use as before
              Object.keys(columns)
                .filter((column) =>
                  Object.keys(measureColumnFilter).includes(column)
                )
                .map((c) => ({
                  name: c,
                  caption: measureColumnFilter?.[c] ?? c,
                }))
          : // If null, fallback to numeric columns
            Object.keys(columns)
              .filter(
                (column) =>
                  columns[column].dtype === "Float64" ||
                  columns[column].dtype === "Int64"
              )
              .map((c) => ({ name: c, caption: c }))
      );
      setFilterColumns(
        Object.keys(columns)
          .filter(
            (column) =>
              columns[column].dtype === "String" ||
              columns[column].dtype === "Boolean"
          )
          .map((c) => ({
            name: c,
            caption: c,
            unique_values: columns?.[c]?.unique_values ?? [],
          }))
      );

      // Set date columns based on dtype from schema
      setColumnSchema(columns);

      const stringCols = Object.keys(columns).filter(c => columns[c].dtype === "String");
      console.info(
        "[SchemaSubscription] Processed schema:",
        "total columns:", Object.keys(columns).length,
        "String columns:", stringCols.length,
        "sample String cols:", stringCols.slice(0, 5)
      );
    },
    onError: (error) => {
      console.error("[SchemaSubscription] onError fired:", error?.message, error);
      // Check if this is a network error (handled gracefully by authentication service)
      const isNetworkError = error?.message?.includes("Network unavailable");

      if (isNetworkError) {
        // Network errors are handled gracefully by authentication service
        // Just show loading state and let the service handle retries
        console.warn(
          "DatePivotControllerContainer: Network error, will retry automatically"
        );
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 5000); // 5 seconds for network errors
      } else {
        // For other errors, log and use shorter retry delay
        console.error("DatePivotControllerContainer: error", error);
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 3000);
      }
    },
    skip: isSubscriptionSkipped,
  });

  // Use filter key order for specific views
  let dynamicMeasureColumns = measureColumns;
  if (
    viewControllerConfig.title === "Forecast Tracking" &&
    viewControllerConfig.measureColumnFilter != null &&
    typeof viewControllerConfig.measureColumnFilter !== "function"
  ) {
    const measureColumnRecord =
      viewControllerConfig.measureColumnFilter as Record<string, string>;
    dynamicMeasureColumns = Object.keys(measureColumnRecord)
      .filter((m) => measureColumns.some((mc: any) => mc.name === m))
      .map((m) => ({
        name: m,
        caption: measureColumnRecord[m],
      }));
  } else if (
    (viewControllerConfig.title.startsWith("Inventory Health") ||
      viewControllerConfig.title.startsWith("Raw Inventory Health")) &&
    viewControllerConfig.measureColumnFilter != null
  ) {
    if (typeof viewControllerConfig.measureColumnFilter === "function") {
      // If it's a function, call with params and use the result
      const filterResult = viewControllerConfig.measureColumnFilter(params);
      dynamicMeasureColumns = filterResult
        ? Object.keys(filterResult).map((m) => ({
            name: m,
            caption: filterResult[m],
          }))
        : measureColumns;
    } else {
      // If it's a Record, use as before
      const measureColumnRecord =
        viewControllerConfig.measureColumnFilter as Record<string, string>;
      dynamicMeasureColumns = Object.keys(measureColumnRecord).map((m) => ({
        name: m,
        caption: measureColumnRecord[m],
      }));
    }
  }

  return (
    <TransformedCustomViewControlsComponent<T>
      viewControllerConfig={viewControllerConfig}
      isLoading={controllerLoading}
      params={params}
      setParams={setParams}
      dateHierarchyColumns={dateHierarchyColumns}
      segmentColumns={segmentColumns}
      measureColumns={dynamicMeasureColumns}
      filterColumns={filterColumns}
      columnSchema={columnSchema}
      parsedData={parsedData}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
      sortConfig={sortConfig}
      setSortConfig={setSortConfig}
      onBackendExport={onBackendExport}
      inMemoryArrowTable={inMemoryArrowTable}
    />
  );
};
